import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { otcVerify } from '@/lib/otcApi';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle2, AlertTriangle, XCircle, Camera, Mic, Shield } from 'lucide-react';

export default function CandidateLanding() {
  const { otc } = useParams();
  const navigate = useNavigate();
  const { data } = useQuery({
    queryKey: ['session', otc],
    queryFn: () => otcVerify(otc),
    retry: false,
  });
  if (!data) return <FullScreen><p className="text-sm text-muted-foreground">Validating link…</p></FullScreen>;

  const StateView = () => {
    if (data.state === 'invalid') return <Notice icon={XCircle} colour="rose" title="This link is not valid" body="The one-time code is incorrect, malformed, or never existed." />;
    if (data.state === 'expired') return <Notice icon={AlertTriangle} colour="amber" title="This link has expired" body="Please contact the Kadel Labs HR team for a new invitation." />;
    if (data.state === 'already_submitted' || data.state === 'consumed') return <Notice icon={CheckCircle2} colour="emerald" title="You have already submitted this session" body="Thank you. The HR team will be in touch with next steps." />;
    const stage = data.stage_code;
    const qCount = (data.questions || []).length;
    const isVideoBot = stage === 'VIDEOBOT';
    const isTechAssessment = stage === 'TECH_ASSESSMENT';
    const sessionPath = isVideoBot ? 'videobot' : isTechAssessment ? 'tech-assessment' : 'mcq';
    const stageTitle = isVideoBot ? 'Video Bot Interview' : isTechAssessment ? 'Tech Assessment' : 'IQ Assessment';
    const beginLabel = isVideoBot ? 'Video Bot' : isTechAssessment ? 'Tech Assessment' : 'IQ Assessment';
    const sebRequired = Boolean(data.seb_required) && !data.seb_verified;
    const questionHint = sebRequired
      ? 'Question count is shown after you open the exam in Safe Exam Browser.'
      : null;
    const durationSeconds = isTechAssessment
      ? (data.tech_total_seconds || data.mcq_total_seconds || 0)
      : (data.mcq_total_seconds || 0);

    return (
      <Card className="card-elevated max-w-xl w-full">
        <CardContent className="p-7">
          <p className="text-xs font-semibold uppercase tracking-wider text-primary">Kadel Labs Interview</p>
          <h1 className="mt-1 text-2xl font-semibold">Welcome, {data.candidate.full_name}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{[data.candidate.role, data.candidate.department].filter(Boolean).join(' · ') || 'Interview'}</p>
          <div className="mt-5 rounded-xl border bg-secondary/40 p-4">
            <p className="text-sm font-semibold">{stageTitle}</p>
            <ul className="mt-2 space-y-1.5 text-sm text-muted-foreground">
              {isVideoBot ? (
                <>
                  <li>· You will be asked {qCount} questions, one at a time.</li>
                  <li>· Each question has 30 seconds to prepare and up to 60 seconds to record.</li>
                  <li>· You cannot skip or revisit previous questions.</li>
                </>
              ) : (
                <>
                  <li>· {sebRequired ? 'Multiple choice questions' : `${qCount} multiple choice questions`} · {Math.floor(durationSeconds / 60)} minutes total.</li>
                  <li>· You can navigate freely; assessment auto-submits when the timer expires.</li>
                  <li>· Independent attempt expected.</li>
                  {questionHint && <li>· {questionHint}</li>}
                </>
              )}
            </ul>
          </div>
          {sebRequired && (
            <div className="mt-4 rounded-xl border bg-card p-4 space-y-3" data-testid="seb-landing-instructions">
              <div className="flex items-center gap-2">
                <Shield className="h-4 w-4 text-primary" />
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Safe Exam Browser required</p>
              </div>
              <p className="text-sm text-muted-foreground">
                Install Safe Exam Browser, download your exam configuration, then open the .seb file.
                Do not attempt this assessment in a normal browser.
              </p>
              <div className="flex flex-wrap gap-2">
                {data.seb_download_url_windows && (
                  <Button asChild variant="outline" size="sm">
                    <a href={data.seb_download_url_windows} target="_blank" rel="noreferrer" data-testid="landing-seb-windows">SEB for Windows</a>
                  </Button>
                )}
                {data.seb_download_url_macos && (
                  <Button asChild variant="outline" size="sm">
                    <a href={data.seb_download_url_macos} target="_blank" rel="noreferrer" data-testid="landing-seb-macos">SEB for macOS</a>
                  </Button>
                )}
                {data.seb_config_url && (
                  <Button asChild size="sm">
                    <a href={data.seb_config_url} data-testid="landing-seb-config">Download .seb config</a>
                  </Button>
                )}
              </div>
            </div>
          )}
          {isVideoBot && (
            <div className="mt-4 rounded-xl border bg-card p-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Device check</p>
              <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                <div className="flex items-center gap-2"><Camera className="h-4 w-4 text-emerald-600" /> Camera ready</div>
                <div className="flex items-center gap-2"><Mic className="h-4 w-4 text-emerald-600" /> Microphone ready</div>
              </div>
            </div>
          )}
          <div className="mt-6 flex items-center justify-end gap-2">
            <Link to="/login" className="text-sm text-muted-foreground">Cancel</Link>
            {!sebRequired && (
              <Button onClick={() => navigate(`/candidate/${otc}/${sessionPath}`)} data-testid="otc-continue-button">
                Begin {beginLabel}
              </Button>
            )}
          </div>
          <p className="mt-4 text-[11px] text-muted-foreground">This session is recorded. By proceeding you confirm independent attempt.</p>
        </CardContent>
      </Card>
    );
  };

  return <FullScreen><StateView /></FullScreen>;
}

function FullScreen({ children }) {
  return (
    <div className="hero-glow min-h-screen">
      <div className="mx-auto flex min-h-screen max-w-5xl items-center justify-center px-6 py-12" data-testid="otc-state">
        {children}
      </div>
    </div>
  );
}

function Notice({ icon: Icon, colour, title, body }) {
  const c = { rose: 'text-rose-700 bg-rose-50 border-rose-200', amber: 'text-amber-800 bg-amber-50 border-amber-200', emerald: 'text-emerald-700 bg-emerald-50 border-emerald-200' }[colour];
  return (
    <Card className="card-elevated max-w-xl w-full"><CardContent className="p-7">
      <span className={`inline-flex h-10 w-10 items-center justify-center rounded-full border ${c}`}><Icon className="h-5 w-5" /></span>
      <h1 className="mt-3 text-xl font-semibold">{title}</h1>
      <p className="mt-1 text-sm text-muted-foreground">{body}</p>
      <Link to="/login" className="mt-4 inline-block text-sm font-medium text-primary">Return to sign in</Link>
    </CardContent></Card>
  );
}
